import type { Square, Vec2 } from '@chessshooter/shared';
import type { Viewport } from '../camera';

// Where the board sits on screen and how squares map to points: pure geometry, tested without a DOM
// like camera.ts.

export interface BoardLayout {
  /** top-left corner, CSS px */
  x: number;
  y: number;
  /** side of the board */
  size: number;
  /** side of one square */
  square: number;
  /** black at the bottom (the own colour is always at the bottom) */
  flipped: boolean;
}

export interface BoardLayoutOptions {
  /** CSS px of the HUD bar at the top */
  topInset: number;
  /** gap to the screen edges */
  margin: number;
  /** strip kept free right above the board for the status line */
  statusHeight: number;
}

const FILES = 'abcdefgh';

/**
 * The largest board that fits below the HUD and the status strip: as wide as the screen in portrait,
 * as high as the remaining space in landscape, centred in what is left. The square is an integer
 * number of pixels so that lines stay crisp.
 */
export function layoutBoard(vp: Viewport, flipped: boolean, opts: BoardLayoutOptions): BoardLayout {
  const availW = Math.max(8, vp.width - 2 * opts.margin);
  const availH = Math.max(8, vp.height - opts.topInset - opts.statusHeight - 2 * opts.margin);
  const square = Math.max(1, Math.floor(Math.min(availW, availH) / 8));
  const size = square * 8;
  return {
    x: Math.round((vp.width - size) / 2),
    y: Math.round(opts.topInset + opts.statusHeight + opts.margin + (availH - size) / 2),
    size,
    square,
    flipped,
  };
}

/** file 0 = a … 7 = h, rank 0 = 1 … 7 = 8 */
export function squareOf(file: number, rank: number): Square {
  return `${FILES[file]}${rank + 1}` as Square;
}

export function fileRankOf(square: Square): { file: number; rank: number } {
  return { file: FILES.indexOf(square[0]), rank: Number(square[1]) - 1 };
}

/** Top-left corner of a square. */
export function squareOrigin(layout: BoardLayout, square: Square): Vec2 {
  const { file, rank } = fileRankOf(square);
  const col = layout.flipped ? 7 - file : file;
  const row = layout.flipped ? rank : 7 - rank;
  return { x: layout.x + col * layout.square, y: layout.y + row * layout.square };
}

export function squareCenter(layout: BoardLayout, square: Square): Vec2 {
  const o = squareOrigin(layout, square);
  return { x: o.x + layout.square / 2, y: o.y + layout.square / 2 };
}

/** The square under a point, or null off the board. */
export function squareAt(layout: BoardLayout, p: Vec2): Square | null {
  const col = Math.floor((p.x - layout.x) / layout.square);
  const row = Math.floor((p.y - layout.y) / layout.square);
  if (col < 0 || col > 7 || row < 0 || row > 7) return null;
  const file = layout.flipped ? 7 - col : col;
  const rank = layout.flipped ? row : 7 - row;
  return squareOf(file, rank);
}
